import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FaInfoCircle, FaPlus, FaRegCalendarCheck, FaTiktok, FaInstagram, FaSync, FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import { MdDragIndicator, MdDelete, MdChevronLeft, MdChevronRight, MdKeyboardArrowUp, MdKeyboardArrowDown, MdError, MdWarning } from 'react-icons/md';
import Textarea from '../Tools/Textarea';
import Modal from '../Modal/Modal';
import ConfirmModal from '../Modal/ConfirmModal';
import CheckboxBtn from './Checkbox/Checkboxbtn';
import axios from 'axios';
import HorizontalTabs from '../Navigation/MenuTabs/HorizontalTabs';
import { v4 as uuidv4 } from 'uuid';
import { LuSend } from 'react-icons/lu';
import MultipleUploadArea from './UploadForm/components/MultipleUploadArea';
import { Button } from '../../ui/button';
import { useWorkspace } from '../../context/WorkspaceContext';
import { MdOutlinePhotoLibrary } from 'react-icons/md';
import TikTokSettings from './UploadForm/components/TikTokSettings';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

const CarouselUploadForm = React.memo(({ draftFormData, onFormAction, onQuotaRefresh, isUploadLimitReached, quotaLoading }) => {
  const { activeWorkspace, idToken, lockWorkspaceChange, unlockWorkspaceChange } = useWorkspace();
  const [openModal, setOpenModal] = useState(false);
  const [userSocials, setUserSocials] = useState([]);
  const [isModalScheduleOpen, setIsModalScheduleOpen] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [warningMessages, setWarningMessages] = useState([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [actionToConfirm, setActionToConfirm] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isActionInProgress, setIsActionInProgress] = useState(false);

  // Carousel-specific state
  const [carouselImages, setCarouselImages] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);

  const uniquePostId = useMemo(() => uuidv4(), []);
  const [deactivatedFormData, setDeactivatedFormData] = useState([]);

  const siteMenuInitial = useMemo(() => [
    { id: 'draft', label: '', icon: null },
    { id: 'tiktok', label: '', icon: <FaTiktok color='black' /> },
    { id: 'instagram', label: '', icon: <FaInstagram color='pink' /> },
  ], []);

  const [siteMenu, setSiteMenu] = useState([]);
  const [activeMenu, setActiveMenu] = useState('draft');

  const [formData, setFormData] = useState([{
    uploadTo: 'draft',
    draftId: '',
    platformId: '',
    postId: uniquePostId,
    title: '',
    description: '',
    uploadType: 'carousel',
    privacyStatus: 'public',
    commentEnabled: true,
    isContentDisclosed: false,
    duetEnabled: true,
    stitchEnabled: true,
    isBranded: false,
    isOwnBrand: false,
    hasBeenEdited: false,
  }]);

  const initialSetupRef = useRef(true);
  const initialLoad = useRef(true);
  const isUpdatingUserSocials = useRef(true);

  const insertDraftMenu = useCallback((data) => {
    const containsDraftStatus = data.some(data => data.status === 'draft');
    if (containsDraftStatus) {
      setSiteMenu(prevSiteMenu => {
        const draftTab = siteMenuInitial.find(menuItem => menuItem.id === 'draft');
        if (draftTab && !prevSiteMenu.some(menuItem => menuItem.id === 'draft')) {
          return [...prevSiteMenu, draftTab];
        }
        return prevSiteMenu;
      });
    }
  }, [siteMenuInitial, setSiteMenu]);

  const getUserSocials = useCallback(async () => {
    if (!idToken || !activeWorkspace.id) return;

    try {
      const response = await axios.get(BACKEND_API_URL + '/api/auth/user/social_media_tokens', {
        params: { workspaceId: activeWorkspace.id },
        headers: { Authorization: `Bearer ${idToken}` },
      });

      response.data.forEach((item) => {
        item.checkbox = false;
      });

      // Only allow TikTok and Instagram for carousel uploads
      const allowedPlatforms = response.data.filter(
        (item) => item.platform === 'tiktok' || item.platform === 'instagram'
      );

      setUserSocials(allowedPlatforms);
      return response.data;
    } catch (error) {
      console.error('Error fetching social media tokens:', error);
    }
  }, [idToken, activeWorkspace.id]);

  const handleRefreshSocials = useCallback(async () => {
    try {
      setIsRefreshing(true);
      await getUserSocials();
    } catch (error) {
      console.error('Error refreshing social media tokens:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [getUserSocials]);

  // Listen for localStorage changes from other tabs (social auth completion)
  useEffect(() => {
    // Function to check if social auth was successful
    const checkAuthSuccess = () => {
      const authSuccess = localStorage.getItem('social_auth_success');
      if (authSuccess) {
        // Clear the flag
        localStorage.removeItem('social_auth_success');
        // Refresh the socials list
        getUserSocials();
      }
    };

    // Function to handle focus events for pending auth
    const handleFocus = () => {
      const pendingAuth = localStorage.getItem('social_auth_pending');
      if (pendingAuth) {
        // When the user returns to this tab, check if there are pending auths and refresh
        getUserSocials();
        localStorage.removeItem('social_auth_pending');
      }
    };

    // Check immediately (for when returning to an already-open tab)
    checkAuthSuccess();

    // Set up event listener for storage changes
    const handleStorageChange = (e) => {
      if (e.key === 'social_auth_success') {
        checkAuthSuccess();
      }
    };

    // Listen for when the window regains focus
    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorageChange);
    
    // Clean up the event listeners
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [getUserSocials]);

  useEffect(() => {
    lockWorkspaceChange();
    if (initialSetupRef.current && activeWorkspace.id && idToken) {
      getUserSocials();
      initialSetupRef.current = false;
    }
    return () => unlockWorkspaceChange();
  }, [getUserSocials, activeWorkspace.id, idToken, lockWorkspaceChange, unlockWorkspaceChange]);

  // Handle loading carousel images from draftFormData when editing
  useEffect(() => {
    if (initialLoad.current && draftFormData && draftFormData.length > 0) {
      // Check if any draft has carousel images
      const draftWithCarousel = draftFormData.find(draft => 
        draft.carouselImages && Array.isArray(draft.carouselImages) && draft.carouselImages.length > 0
      );

      if (draftWithCarousel) {
        // Convert the carousel images to the format expected by the component
        const convertedImages = draftWithCarousel.carouselImages.map((image, index) => ({
          id: image.id || uuidv4(),
          firebasePath: image.firebasePath || image.thumbnail,
          thumbnail: image.thumbnail || image.firebasePath,
          videoPath: image.videoPath || null
        }));

        setCarouselImages(convertedImages);
      }

      // Process form data similar to UploadForm
      const hasMatchingPlatform = draftFormData.some(draft =>
        userSocials.some(social => social.platform_id === draft.platformId || draft.uploadTo === 'draft')
      );

      if (hasMatchingPlatform) {
        const updatedDraftFormData = draftFormData.map((data) => ({
          ...data,
          postId: uniquePostId,
        }));
        setFormData(updatedDraftFormData);
        insertDraftMenu(updatedDraftFormData);
        initialLoad.current = false;
      }
    }
  }, [draftFormData, uniquePostId, userSocials, insertDraftMenu]);

  // Handle file uploads
  const handleFileSelect = useCallback((firebaseVideoPath, firebaseThumbnail) => {
    // Block uploads if we're at the maximum
    if (carouselImages.length >= 10) {
      return; // This will be handled by the MultipleUploadArea's validation
    }

    const newImage = {
      id: uuidv4(),
      firebasePath: firebaseVideoPath,
      thumbnail: firebaseThumbnail,
      videoPath: firebaseVideoPath.includes('firebase') ? firebaseVideoPath.split('/').pop() : null
    };

    setCarouselImages(prev => {
      const updated = [...prev, newImage];
      // Update form data to mark as edited
      setFormData(prevFormData => prevFormData.map(item => ({
        ...item,
        hasBeenEdited: true,
      })));
      return updated;
    });
  }, [carouselImages.length]);

  // Reorder images
  const moveImage = useCallback((fromIndex, toIndex) => {
    setCarouselImages(prev => {
      const updated = [...prev];
      const [movedItem] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedItem);
      return updated;
    });
  }, []);

  // Remove image
  const removeImage = useCallback((imageId) => {
    setCarouselImages(prev => prev.filter(img => img.id !== imageId));
    setCurrentPreviewIndex(prev => Math.max(0, prev - 1));
  }, []);

  // Clear all images
  const clearAllImages = useCallback(() => {
    setCarouselImages([]);
    setCurrentPreviewIndex(0);
  }, []);

  const handleFormDataChange = useCallback((platform, newFormData) => {
    setFormData(prevFormData =>
      prevFormData.map(data =>
        data.platformId === platform || data.uploadTo === platform
          ? { ...data, ...newFormData, hasBeenEdited: true }
          : data
      )
    );
  }, []);

  const findPlatformData = (platform) => {
    return formData.find(data => data.platformId === platform) || formData[0];
  };

  const handleErrors = useCallback((errors, warnings) => {
    setErrorMessages(errors || []);
    setWarningMessages(warnings || []);
  }, []);


  const newFormDataRef = useRef(null);

  // Define handleActionClick before using it in useEffect
  const proceedWithAction = useCallback(async (action) => {
    setIsActionInProgress(true);
    
    try {
      let editedFormData = formData.filter(
        (data) => data.hasBeenEdited || data.description !== ''
      );

      if (editedFormData.length >= 2) {
        editedFormData = editedFormData.filter((data) => data.uploadTo !== 'draft');
      }

      // Add carousel images to the form data
      const updatedFormData = editedFormData.map(data => ({
        ...data,
        carouselImages: carouselImages
      }));

      await onFormAction(action, updatedFormData);
    } catch (error) {
      console.error('Action failed:', error);
    } finally {
      setIsActionInProgress(false);
    }
  }, [formData, onFormAction, carouselImages]);

  const handleActionClick = useCallback(async (action) => {
    // For 'upload', check warnings immediately
    if (action === 'upload' && warningMessages.length > 0 && errorMessages.length === 0) {
      setActionToConfirm(action);
      setIsConfirmModalOpen(true);
    } else if (action !== 'schedule') { // For 'draft' or 'upload' with errors/no warnings
      await proceedWithAction(action);
    }
    // 'schedule' action is handled by the useEffect below
  }, [warningMessages, errorMessages, proceedWithAction]);

  const socialCheckboxesActivate = useCallback((userSocial) => {
    setUserSocials((prevUserSocials) => {
      // Toggle the checkbox state for the selected social platform
      const updatedUserSocials = prevUserSocials.map((social) => {
        if (social.platform_id === userSocial.platform_id) {
          return { ...social, checkbox: !social.checkbox };
        }
        return social;
      });

      // Update formData based on the checkbox state
      setFormData((prevFormData) => {
        const draftEntry = prevFormData.find(data => data.uploadTo === 'draft');

        if (!userSocial.checkbox) {
          // If the checkbox is now checked, add the platform data back to formData
          const existingData = deactivatedFormData.find(data => data.platformId === userSocial.platform_id);
          if (existingData) {
            const existingObj = prevFormData.find(data => data.platformId === userSocial.platform_id);
            if (!existingObj) {
              prevFormData = [...prevFormData, existingData];
              setDeactivatedFormData((prev) => prev.filter(data => data.platformId !== userSocial.platform_id));
            }
          } else {
            const existingObj = prevFormData.find(data => data.platformId === userSocial.platform_id);
            if (!existingObj) {
              prevFormData = [...prevFormData, {
                uploadTo: userSocial.platform,
                draftId: '',
                postId: uniquePostId,
                title: '',
                description: draftEntry ? draftEntry.description : '',
                uploadType: 'carousel',
                privacyStatus: 'public',
                commentEnabled: true,
                isContentDisclosed: false,
                duetEnabled: true,
                stitchEnabled: true,
                isBranded: false,
                isOwnBrand: false,
                hasBeenEdited: false,
                platformId: userSocial.platform_id,
              }];
            }
          }
        } else {
          // If the checkbox is now unchecked, remove the platform data from formData
          const dataToDeactivate = prevFormData.find(data => data.uploadTo === userSocial.platform);
          if (dataToDeactivate) {
            setDeactivatedFormData((prev) => [...prev, dataToDeactivate]);
            prevFormData = prevFormData.filter(data => data.platformId !== userSocial.platform_id);
          }
        }

        // Ensure there's always at least one draft entry in formData
        if (prevFormData.length === 0) {
          prevFormData.push({
            uploadTo: 'draft',
            draftId: '',
            postId: uniquePostId,
            title: '',
            description: '',
            uploadType: 'carousel',
            privacyStatus: 'public',
            commentEnabled: true,
            isContentDisclosed: false,
            duetEnabled: true,
            stitchEnabled: true,
            isBranded: false,
            isOwnBrand: false,
            hasBeenEdited: false,
            platformId: '',
          });
        }

        // Update postId for all entries in formData
        return prevFormData.map((data) => ({
          ...data,
          postId: uniquePostId,
        }));
      });

      return updatedUserSocials;
    });
  }, [deactivatedFormData, uniquePostId]);

  // Update userSocials based on draftFormData (similar to UploadForm)
  useEffect(() => {
    if (isUpdatingUserSocials.current && userSocials.length > 0 && draftFormData.length > 0) {
      isUpdatingUserSocials.current = false;
      const updatedUserSocials = userSocials.map(userSocial => {
        const isMatched = draftFormData.some(upload => upload.platformId === userSocial.platform_id);
        return { ...userSocial, checkbox: isMatched };
      });

      // Compare arrays to see if update is needed
      const areArraysDifferent = updatedUserSocials.some((item, index) =>
        item.checkbox !== userSocials[index]?.checkbox ||
        item.platform_id !== userSocials[index]?.platform_id
      );

      if (areArraysDifferent) {
        setUserSocials(updatedUserSocials);
      }
    }
  }, [draftFormData, userSocials]);

  // Update siteMenu based on userSocials
  useEffect(() => {
    if (userSocials.length > 0) {
      const atLeastOneChecked = userSocials.some((social) => social.checkbox === true);
      let isDraft = false;

      if (formData && formData.length > 0) {
        isDraft = formData.some(upload => upload.uploadTo === 'draft');
      }

      let updatedSiteMenu = [];

      if (atLeastOneChecked) {
        updatedSiteMenu = siteMenuInitial
          .filter(menuItem =>
            userSocials.some(social => social.platform === menuItem.id && social.checkbox === true)
          )
          .flatMap(menuItem => {
            const matchedSocials = userSocials.filter(
              social => social.platform === menuItem.id && social.checkbox === true
            );

            const matched = matchedSocials.map(social => ({
              ...menuItem,
              id: social.platform_id,
              label: `${social.username.substring(0, 6)}..`
            }));
            return matched;
          });
      }
      
      if (!atLeastOneChecked) {
        const draftTab = { id: 'draft', label: 'Draft', icon: null };
        if (!updatedSiteMenu.some(menuItem => menuItem.id === 'draft')) {
          updatedSiteMenu.push(draftTab);
        }
      }

      // Compare menus without using JSON.stringify
      const areMenusDifferent =
        updatedSiteMenu.length !== siteMenu.length ||
        updatedSiteMenu.some((item, index) =>
          item.id !== siteMenu[index]?.id ||
          item.label !== siteMenu[index]?.label
        );

      if (areMenusDifferent) {
        setSiteMenu(updatedSiteMenu);
      }

      const isActiveMenuValid = updatedSiteMenu.some(menuItem => menuItem.id === activeMenu);

      if (!isActiveMenuValid) {
        if (updatedSiteMenu.some(menuItem => menuItem.id === 'draft')) {
          if (activeMenu !== 'draft') {
            setActiveMenu('draft');
          }
        } else if (updatedSiteMenu.length > 0) {
          if (activeMenu !== updatedSiteMenu[0].id) {
            setActiveMenu(updatedSiteMenu[0].id);
          }
        } else {
          if (activeMenu !== '') {
            setActiveMenu('');
          }
        }
      }
    }
  }, [userSocials, siteMenuInitial, activeMenu, siteMenu, formData]);

  const closeModal = (action) => {
    if (action instanceof Date) {
      const newFormData = formData.map((data) => ({
        ...data,
        schedule: action,
      }));
      setFormData(newFormData)
      newFormDataRef.current = newFormData;
    }

    setOpenModal(false);
    setIsModalScheduleOpen(false);
  };

  useEffect(() => {
    if (newFormDataRef.current) { // This means a schedule date was selected
      if (warningMessages.length > 0 && errorMessages.length === 0) {
        setActionToConfirm('schedule');
        setIsConfirmModalOpen(true);
        // newFormDataRef.current will be cleared on confirm/cancel
      } else {
        // If errors exist, proceedWithAction won't effectively publish/schedule due to disabled buttons,
        // but this logic ensures newFormDataRef.current is cleared.
        // Or if no warnings and no errors, proceed.
        const executeSchedule = async () => {
          await proceedWithAction('schedule');
          newFormDataRef.current = null;
        };
        executeSchedule();
      }
    }
  }, [formData, warningMessages, errorMessages, proceedWithAction]); // formData also triggers this, then we check warnings

  // Helper function for warning colors (can be moved to a utility file if used elsewhere)
  const getWarningDisplayColor = (color) => {
    switch (color) {
      case 'red': return '#FEE2E2'; // bg-red-100
      case 'yellow': return '#FEF3C7'; // bg-yellow-100
      default: return '#E5E7EB'; // bg-gray-200 (fallback)
    }
  };

  const handleConfirm = async () => {
    if (actionToConfirm) {
      await proceedWithAction(actionToConfirm);
    }
    if (actionToConfirm === 'schedule') {
      newFormDataRef.current = null; // Clear ref if action was schedule
    }
    setIsConfirmModalOpen(false);
    setActionToConfirm(null);
  };

  const handleCancelConfirm = () => {
    if (actionToConfirm === 'schedule') {
      // Clear the schedule from formData if it was set by closeModal
      setFormData(prevFormData =>
        prevFormData.map(data => {
          const { schedule, ...rest } = data; // Remove schedule property
          return rest;
        })
      );
      newFormDataRef.current = null; // Important to clear this ref
    }
    setIsConfirmModalOpen(false);
    setActionToConfirm(null);
  };

  return (
    <>
      <div className='flex flex-col h-full pb-1'>
        <div className='flex flex-col md:flex-row w-full h-full overflow-y-auto overflow-x-hidden'>
          {/* LEFT PANEL */}
          <div className={`p-5 !w-full md:w-3/5 md:min-w-0`}>
            <div className='my-4'>
              <HorizontalTabs tabs={siteMenu} onTabChange={setActiveMenu} activeTab={activeMenu} />
            </div>

            <div className='w-full'>
              <CarouselForm
                formData={findPlatformData(activeMenu)}
                onFormDataChange={(newFormData) => handleFormDataChange(activeMenu, newFormData)}
                userSocials={userSocials}
                carouselImages={carouselImages}
                onFileSelect={handleFileSelect}
                onClearAll={clearAllImages}
                socialCheckboxesActivate={socialCheckboxesActivate}
                setOpenModal={setOpenModal}
                handleRefreshSocials={handleRefreshSocials}
                isRefreshing={isRefreshing}
              />
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className='!w-full md:!w-2/5 md:border-l md-border-l-0 p-5 md:border-t-0 border-t border-gray-300'>
            {/* Validation Messages */}
            {(errorMessages.length > 0 || warningMessages.length > 0) && (
              <div className="mb-4">
                {errorMessages.map((error, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2 p-3 rounded-lg bg-red-50 border border-red-200">
                    {error.icon && React.createElement(error.icon, { size: 20, className: "flex-shrink-0 text-red-500" })}
                    <span className="text-red-700 text-sm">{error.message}</span>
                  </div>
                ))}
                {warningMessages.map((warning, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2 p-3 rounded-lg bg-yellow-50 border border-yellow-200">
                    {warning.icon && React.createElement(warning.icon, { size: 20, className: "flex-shrink-0 text-yellow-500" })}
                    <span className="text-yellow-700 text-sm">{warning.message}</span>
                  </div>
                ))}
              </div>
            )}
            
            <CarouselPreview
              carouselImages={carouselImages}
              currentIndex={currentPreviewIndex}
              onIndexChange={setCurrentPreviewIndex}
              onMoveImage={moveImage}
              onRemoveImage={removeImage}
              formData={formData}
              userSocials={userSocials}
              onError={handleErrors}
            />
          </div>
        </div>

        <ActionButtons
          handleActionClick={handleActionClick}
          setIsModalScheduleOpen={setIsModalScheduleOpen}
          disableButtons={errorMessages.length > 0}
          isActionInProgress={isActionInProgress}
        />
      </div>

      {/* MODALS */}
      {isModalScheduleOpen && (
        <Modal
          open={isModalScheduleOpen}
          otherInfo={formData[0].schedule ? new Date(formData[0].schedule) : new Date()}
          closeModal={() => setIsModalScheduleOpen(false)}
          type={'ScheduledModal'}
        />
      )}
      {openModal && (
        <Modal
          open={openModal}
          closeModal={closeModal}
          type={'ConnectSocialsModal'}
        />
      )}
      {isConfirmModalOpen && (
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          onClose={handleCancelConfirm}
          onConfirm={handleConfirm}
          title="Confirm Action"
          message={
            <>
              <p>The following warnings were found. Do you want to proceed with {actionToConfirm}ing?</p>
              <div className="mt-2 text-sm max-h-60 overflow-y-auto">
                {warningMessages.map((warning, index) => (
                  <div key={index} className="flex items-center gap-2 mt-1 p-2 rounded text-gray-700" style={{ backgroundColor: getWarningDisplayColor(warning.color) }}>
                    {warning.icon && React.createElement(warning.icon, { size: 20, className: "flex-shrink-0" })}
                    <span>{warning.message}</span>
                  </div>
                ))}
              </div>
            </>
          }
        />
      )}
    </>
  );
});

// Social Media Checkboxes Component
const SocialMediaCheckboxes = React.memo(({
  userSocials,
  socialCheckboxesActivate,
  handleRefreshSocials,
  isRefreshing,
  setOpenModal
}) => {
  const handleToggleAllSocials = () => {
    if (userSocials.length === 0) return;

    const areAllCurrentlyChecked = userSocials.every(social => social.checkbox);

    userSocials.forEach(social => {
      // If we want to uncheck all (because all are currently checked), and this one is checked, call activate
      if (areAllCurrentlyChecked && social.checkbox) {
        socialCheckboxesActivate(social);
      }
      // If we want to check all (because not all are currently checked), and this one is not checked, call activate
      else if (!areAllCurrentlyChecked && !social.checkbox) {
        socialCheckboxesActivate(social);
      }
    });
  };

  return (
    <div className={`flex flex-col mb-5 dashboard-container !p-0`}>
      <div className='flex justify-between items-center'>
        <div className='flex items-center gap-2'>
          <p className='text-md font-semibold'>Select your socials</p>
          <span className='text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full'>
            Carousel Mode
          </span>
        </div>
        <div className='flex gap-2 items-center'>
          {userSocials.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleAllSocials}
            >
              {userSocials.every(s => s.checkbox) ? <FaCheckSquare size={14} /> : <FaRegSquare size={14} />}
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={isRefreshing ? null : handleRefreshSocials}
            disabled={isRefreshing}
          >
            <FaSync size={10} className={isRefreshing ? 'animate-spin' : ''} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpenModal(true)}
          >
            <FaPlus size={10} />
            <span className='text-[11px]'>Connect</span>
          </Button>
        </div>
      </div>
      <div className={`flex w-full gap-4 px-1 py-2 overflow-x-auto overflow-y-hidden flex-wrap`}>
        {userSocials.length > 0 ? userSocials.map((socialMedia, index) => (
          <div
            key={socialMedia.platform_id || index}
            className={`${socialMedia.checkbox ? 'opacity-100' : 'opacity-40'} relative transition-transform transform hover:scale-105 hover:opacity-100`}
            onClick={() => socialCheckboxesActivate(socialMedia)}
          >
            <CheckboxBtn
              name={socialMedia.username}
              platform={socialMedia.platform}
              logo={socialMedia.logo}
              active={socialMedia.checkbox}
            />
          </div>
        )) : (
          <div className='flex flex-col items-center justify-center w-full h-full gap-3'>
            <p className='text-gray-500 text-center mt-3'>No carousel-compatible socials connected</p>
            <p className='text-gray-500 italic text-sm text-center opacity-70'>Connect Instagram or TikTok to start creating carousels</p>
          </div>
        )}
      </div>
    </div>
  );
});

// Carousel Form Component
const CarouselForm = React.memo(({
  formData,
  onFormDataChange,
  userSocials,
  carouselImages,
  onFileSelect,
  onClearAll,
  socialCheckboxesActivate,
  setOpenModal,
  handleRefreshSocials,
  isRefreshing
}) => {
  const [isLibraryModalOpen, setIsLibraryModalOpen] = useState(false);

  const closeLibraryModal = (path) => {
    setIsLibraryModalOpen(false);
    if (typeof path === 'string' && path.trim() !== '') {
      onFileSelect(path, null);
    }
  };

  const handleInputChange = (field, value) => {
    const newFormData = { [field]: value };
    onFormDataChange(newFormData);
  };

  const handleDescriptionChange = (value) => {
    handleInputChange('description', value);
  };

  return (
    <>
      <form onClick={(e) => e.stopPropagation()}>
        <div className='py-2 w-full md:w-2/3 mx-auto'>
          <Textarea 
            onDescriptionChange={handleDescriptionChange} 
            initialDescription={formData.description}
            placeholder={'Write your carousel caption'}
            formData={formData}
            rows={6}
            maxLength={500}
          />
        </div>

        <div className='border-b border-t py-2 border-gray-300 mb-4 w-full xl:w-2/3 mx-auto'>
          <SocialMediaCheckboxes
            userSocials={userSocials}
            socialCheckboxesActivate={socialCheckboxesActivate}
            setOpenModal={setOpenModal}
            handleRefreshSocials={handleRefreshSocials}
            isRefreshing={isRefreshing}
          />
        </div>

        {formData.uploadTo === 'tiktok' && (
          <div className='w-full md:w-2/3 mx-auto'>
            <TikTokSettings
              formData={formData}
              handleInputChange={handleInputChange}
              userSocials={userSocials}
            />
          </div>
        )}

        {/* Carousel Images Summary */}
        {carouselImages.length > 0 && (
          <div className={`mb-4 p-3 rounded-lg border w-full md:w-2/3 mx-auto ${
            carouselImages.length >= 10 
              ? 'bg-amber-50 border-amber-200' 
              : 'bg-purple-50 border-purple-200'
          }`}>
            <div className="flex justify-between items-start">
              <div>
                <p className={`font-medium ${
                  carouselImages.length >= 10 ? 'text-amber-800' : 'text-purple-800'
                }`}>
                  {carouselImages.length} image{carouselImages.length === 1 ? '' : 's'} uploaded
                  {carouselImages.length >= 10 && ' (Maximum reached)'}
                </p>
                <p className={`text-xs mt-1 ${
                  carouselImages.length >= 10 ? 'text-amber-600' : 'text-purple-600'
                }`}>
                  {carouselImages.length >= 10 
                    ? 'Remove some images to upload more. Maximum 10 images allowed.'
                    : 'Carousel uploads require 2-10 images for Instagram and TikTok'
                  }
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                type="button"
                className="text-xs ml-2"
                onClick={onClearAll}
              >
                Clear All
              </Button>
            </div>
          </div>
        )}

        {/* Upload Area */}
        <div className="py-2 mx-auto flex flex-col justify-center items-center w-full gap-1 w-full md:w-2/3">
          {carouselImages.length >= 10 ? (
            <div className="w-full p-4 border-2 border-dashed border-amber-300 rounded-lg bg-amber-50 text-center">
              <p className="text-amber-800 font-medium">Maximum images reached (10/10)</p>
              <p className="text-amber-600 text-sm mt-1">Remove some images to upload more</p>
            </div>
          ) : (
            <>
              <div className='flex gap-2 justify-end w-full mb-2'>
                <Button variant="primary" size="md" type="button" className='self-end' onClick={() => setIsLibraryModalOpen(true)}>
                  <MdOutlinePhotoLibrary size={15} />
                  <span className='text-sm text-white'>My Library</span>
                </Button>
              </div>
              
              <MultipleUploadArea 
                onFileSelect={(path, thumbnail) => {
                  if (carouselImages.length < 10) {
                    onFileSelect(path, thumbnail);
                  }
                }} 
                uploadType="post" 
              />
            </>
          )}
        </div>
      </form>

      {isLibraryModalOpen && (
        <Modal
          open={isLibraryModalOpen}
          closeModal={closeLibraryModal}
          type={'LibraryModal'}
          otherInfo={'post'}
        />
      )}
    </>
  );
});

// Carousel Preview Component
const CarouselPreview = React.memo(({
  carouselImages,
  currentIndex,
  onIndexChange,
  onMoveImage,
  onRemoveImage,
  formData,
  userSocials,
  onError
}) => {
  const currentImage = carouselImages[currentIndex];

  // Validation logic
  useEffect(() => {
    const errors = [];
    const warnings = [];

    // Check if no socials are selected
    const noActiveSocials = userSocials.every(social => !social.checkbox);
    if (noActiveSocials) {
      errors.push({
        message: 'Select at least one social (Instagram or TikTok) to publish your carousel.',
        color: 'red'
      });
    }

    // Check carousel image count
    if (carouselImages.length === 0) {
      warnings.push({
        message: 'Upload at least 2 images to create a carousel.',
        color: 'yellow'
      });
    } else if (carouselImages.length === 1) {
      warnings.push({
        message: 'Carousel uploads require at least 2 images.',
        color: 'yellow'
      });
    } else if (carouselImages.length > 10) {
      errors.push({
        message: 'Carousel uploads support a maximum of 10 images. Please remove some images.',
        color: 'red'
      });
    } else if (carouselImages.length === 10) {
      warnings.push({
        message: 'You have reached the maximum of 10 images for carousel uploads.',
        color: 'yellow'
      });
    }

    // Check for missing description
    const platformsMissingDescription = formData
      .filter(data => !data.description && data.uploadTo !== 'draft')
      .map(data => data.uploadTo);

    if (platformsMissingDescription.length > 0) {
      warnings.push({
        message: 'Add a caption for your carousel.',
        color: 'yellow'
      });
    }

    // TikTok-specific validation for carousel images
    const tiktokSocial = userSocials.find(social => social.platform === 'tiktok' && social.checkbox === true);
    if (tiktokSocial && carouselImages.length > 0) {
      let processedImages = 0;
      const totalImages = carouselImages.length;
      
      carouselImages.forEach((image, index) => {
        const img = new Image();
        img.onload = () => {
          processedImages++;
          
          if (img.height > 1080) {
            const newError = {
              message: `TikTok carousel image ${index + 1} cannot exceed 1080p (1080 height) pixels. Your image is ${img.width}x${img.height} pixels.`,
              icon: MdError,
              color: 'red'
            };
            
            // Add error to the current errors array
            errors.push(newError);
          }
          
          // Only call onError when all images have been processed
          if (processedImages === totalImages) {
            onError(errors, warnings);
          }
        };
        img.onerror = () => {
          processedImages++;
          if (processedImages === totalImages) {
            onError(errors, warnings);
          }
        };
        img.src = image.firebasePath;
      });
    } else {
      // If no TikTok social or no images, call onError immediately
      onError(errors, warnings);
    }
  }, [carouselImages, formData, userSocials, onError]);

  const navigateImage = (direction) => {
    if (direction === 'prev') {
      onIndexChange(currentIndex > 0 ? currentIndex - 1 : carouselImages.length - 1);
    } else {
      onIndexChange(currentIndex < carouselImages.length - 1 ? currentIndex + 1 : 0);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <h3 className="text-lg font-semibold mb-4">Carousel Preview</h3>
      
      {carouselImages.length > 0 ? (
        <>
          {/* Main Preview */}
          <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-4 aspect-square">
            <img
              src={currentImage?.firebasePath}
              alt={`Carousel ${currentIndex + 1}`}
              className="w-full h-full object-cover"
            />
            
            {/* Navigation Arrows */}
            {carouselImages.length > 1 && (
              <>
                <button
                  onClick={() => navigateImage('prev')}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 md:p-2 rounded-full hover:bg-black/70 transition-colors touch-manipulation"
                >
                  <MdChevronLeft size={24} className="md:w-5 md:h-5" />
                </button>
                <button
                  onClick={() => navigateImage('next')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 md:p-2 rounded-full hover:bg-black/70 transition-colors touch-manipulation"
                >
                  <MdChevronRight size={24} className="md:w-5 md:h-5" />
                </button>
              </>
            )}
            
            {/* Image Counter */}
            <div className="absolute bottom-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-sm">
              {currentIndex + 1} / {carouselImages.length}
            </div>
          </div>

          {/* Image Reorder List */}
          <div className="flex-1 overflow-y-auto">
            <h4 className="font-medium mb-2">Reorder Images</h4>
            <div className="space-y-2">
              {carouselImages.map((image, index) => (
                <CarouselImageItem
                  key={image.id}
                  image={image}
                  index={index}
                  isActive={index === currentIndex}
                  onMove={onMoveImage}
                  onRemove={onRemoveImage}
                  onSelect={() => onIndexChange(index)}
                  totalImages={carouselImages.length}
                />
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-gray-500">
          <p className="text-center">Upload images to see your carousel preview</p>
          <p className="text-sm text-center mt-2 opacity-70">Images will appear here as you upload them</p>
        </div>
      )}
    </div>
  );
});

// Individual Carousel Image Item Component
const CarouselImageItem = React.memo(({
  image,
  index,
  isActive,
  onMove,
  onRemove,
  onSelect,
  totalImages
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragStart = (e) => {
    setIsDragging(true);
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
    const toIndex = index;
    if (fromIndex !== toIndex) {
      onMove(fromIndex, toIndex);
    }
    setIsDragging(false);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleMoveUp = (e) => {
    e.stopPropagation();
    if (index > 0) {
      onMove(index, index - 1);
    }
  };

  const handleMoveDown = (e) => {
    e.stopPropagation();
    if (index < totalImages - 1) {
      onMove(index, index + 1);
    }
  };

  return (
    <div
      className={`flex items-center gap-3 p-2 rounded border transition-colors cursor-pointer ${
        isActive ? 'border-purple-300 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
      } ${isDragging ? 'opacity-50' : ''}`}
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
      onClick={onSelect}
    >
      {/* Desktop drag handle */}
      <MdDragIndicator className="hidden md:block text-gray-400 cursor-grab" size={16} />
      
      {/* Mobile reorder buttons */}
      <div className="md:hidden flex flex-col gap-1">
        <button
          onClick={handleMoveUp}
          disabled={index === 0}
          className={`p-1 rounded ${
            index === 0 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          <MdKeyboardArrowUp size={16} />
        </button>
        <button
          onClick={handleMoveDown}
          disabled={index === totalImages - 1}
          className={`p-1 rounded ${
            index === totalImages - 1 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          <MdKeyboardArrowDown size={16} />
        </button>
      </div>

      <img
        src={image.firebasePath}
        alt={`Carousel ${index + 1}`}
        className="w-12 h-12 object-cover rounded"
      />
      <div className="flex-1">
        <p className="text-sm font-medium">Image {index + 1}</p>
        <p className="text-xs text-gray-500">
          <span className="hidden md:inline">Click to preview</span>
          <span className="md:hidden">Tap to preview</span>
        </p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(image.id);
        }}
        className="text-red-500 hover:text-red-700 p-1"
      >
        <MdDelete size={16} />
      </button>
    </div>
  );
});

// Action Buttons Component
const ActionButtons = React.memo(({ handleActionClick, setIsModalScheduleOpen, disableButtons, isActionInProgress }) => {
  const isDisabled = disableButtons || isActionInProgress;
  
  return (
    <div className='flex py-3 relative items-end border-t px-5 overflow-hidden'>
      <div className='flex gap-2 items-end justify-end w-full'>
        <Button
          variant="outline"
          size="xl"
          className={`border-b border-gray-200 px-2 text-gray-700 ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`}
          onClick={() => {
            if (!isDisabled) {
              handleActionClick('draft');
            }
          }}
          disabled={isDisabled}
        >
          {isActionInProgress ? (
            <>
              <FaSync className="animate-spin mr-2" size={14} />
              Saving...
            </>
          ) : (
            'Save Draft'
          )}
        </Button>
        <Button
          variant="secondary"
          size="xl"
          className={`flex gap-1 items-center ${isDisabled ? 'cursor-not-allowed bg-gray-300' : ''}`}
          onClick={() => {
            if (!isDisabled) {
              setIsModalScheduleOpen(true);
            }
          }}
          disabled={isDisabled}
        >
          {isActionInProgress ? (
            <>
              <FaSync className="animate-spin" size={14} />
              Processing...
            </>
          ) : (
            <>
              Schedule
              <FaRegCalendarCheck />
            </>
          )}
        </Button>

        <Button
          variant="default"
          size="xl"
          className={`flex gap-1 items-center ${isDisabled ? 'cursor-not-allowed bg-gray-300' : ''}`}
          onClick={() => {
            if (!isDisabled) {
              handleActionClick('upload');
            }
          }}
          disabled={isDisabled}
        >
          {isActionInProgress ? (
            <>
              <FaSync className="animate-spin" size={14} />
              Publishing...
            </>
          ) : (
            <>
              Publish
              <LuSend />
            </>
          )}
        </Button>
      </div>
    </div>
  );
});

export default CarouselUploadForm; 